import { useRef, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { MoveHorizontal } from "lucide-react";

export const RescueSlider = () => {
  const [pos, setPos] = useState(50);
  const containerRef = useRef(null);
  const dragging = useRef(false);

  const setFromClientX = useCallback((clientX) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    let pct = ((clientX - rect.left) / rect.width) * 100;
    pct = Math.max(0, Math.min(100, pct));
    setPos(pct);
  }, []);

  const onPointerDown = (e) => {
    dragging.current = true;
    setFromClientX(e.clientX);
  };
  const onPointerMove = (e) => {
    if (!dragging.current) return;
    setFromClientX(e.clientX);
  };
  const stop = () => { dragging.current = false; };

  return (
    <section data-testid="rescue-slider-section" className="relative py-24 sm:py-32 border-t border-line">
      <div className="container-x">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <span className="mono-label text-accent">[ Before / After ]</span>
            <h2 className="font-head font-black tracking-tighter text-4xl sm:text-5xl lg:text-6xl mt-4 max-w-2xl text-balance">
              From outdated to outstanding.
            </h2>
          </div>
          <p className="text-text-2 max-w-sm">
            Drag the handle to see how we rescue tired, dated websites and turn them into
            conversion machines.
          </p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          ref={containerRef}
          data-testid="rescue-slider"
          className="relative w-full aspect-[16/10] sm:aspect-[16/9] overflow-hidden border border-line select-none cursor-ew-resize bg-surface-2"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={stop}
          onPointerLeave={stop}
        >
          {/* After (full, underneath) */}
          <img src="/assets/rescue-after.png" alt="Modern redesigned website" draggable={false} className="absolute inset-0 w-full h-full object-cover pointer-events-none" />
          <div className="absolute top-4 right-4 bg-accent text-ink mono-label px-3 py-1 z-10">After</div>

          {/* Before (clipped on top) */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none" style={{ width: `${pos}%` }}>
            <img
              src="/assets/rescue-before.png"
              alt="Outdated website before redesign"
              draggable={false}
              className="absolute inset-0 h-full object-cover max-w-none"
              style={{ width: containerRef.current ? containerRef.current.getBoundingClientRect().width : "100%" }}
            />
            <div className="absolute top-4 left-4 bg-ink/80 border border-line text-white mono-label px-3 py-1">Before</div>
          </div>

          {/* Handle */}
          <div className="absolute top-0 bottom-0 z-20 pointer-events-none" style={{ left: `${pos}%`, transform: "translateX(-50%)" }}>
            <div className="w-0.5 h-full bg-accent" />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-accent text-ink flex items-center justify-center shadow-lg">
              <MoveHorizontal className="w-5 h-5" />
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
